import type {
  FeeSettingsResponse,
  SavedPaymentAccountRefundSummary,
} from "@/api/cashier";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useTranslation } from "react-i18next";
import { Tag } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import {
  SelectableCard,
  type SelectableCardProps,
} from "@/components/selectable-card/SelectableCard";
import { Tooltip } from "@/components/tooltip/Tooltip";
import { withOfflineTooltip } from "@/components/tooltip/withOfflineTooltip";
import { isPaymentMethodOffline } from "@/utils/paymentMethodStatus";
import styles from "./SavedPaymentAccountCard.module.css";

const OfflineTooltip = withOfflineTooltip(Tooltip);

type SavedPaymentAccountCardProps = {
  savedPaymentAccount: SavedPaymentAccountRefundSummary;
  fee?: FeeSettingsResponse | null;
} & Omit<SelectableCardProps, "id">;

export const SavedPaymentAccountCard = ({
  savedPaymentAccount,
  selected,
  children,
  disabled,
  title,
  fee,
  ...cardProps
}: SavedPaymentAccountCardProps) => {
  const { t } = useTranslation();
  const { formatFee } = useFormatAmount();
  const { currencies } = useCurrencies();

  //changed to render spa without counterpart
  if (!savedPaymentAccount) {
    return null;
  }

  let feeLabel: string | null = null;
  if (fee === null) {
    feeLabel = t("withdrawals.fee.no_fee");
  } else if (fee?.type === "percentage") {
    feeLabel = t("withdrawals.fee.percentage", { value: fee.value.amount });
  } else if (fee?.type === "fixed") {
    const currency = currencies?.[fee?.value.code];
    feeLabel = t("withdrawals.fee.fixed", {
      value: formatFee(fee, currency),
    });
  }

  const composedTitle = (
    <div className="ps-flex ps-gap-2 ps-items-center">
      {title ?? savedPaymentAccount.name}
      {feeLabel && (
        <Tag size="sm" bgColor="foreground-neutral" label={feeLabel} />
      )}
    </div>
  );

  const offline = isPaymentMethodOffline(savedPaymentAccount);

  return (
    <OfflineTooltip
      offline={offline}
      disabledReason={savedPaymentAccount.disabled_reason}
    >
      <SelectableCard
        className={clsx(styles.card, selected && styles.cardSelected)}
        disabled={disabled || offline}
        //TODO, double check not sure if we can use that as id
        id={
          savedPaymentAccount.id ||
          `${savedPaymentAccount.payment_method_id}_${savedPaymentAccount.ledger_entries?.[0]?.minimum_amount?.code}`
        }
        title={composedTitle}
        subText={savedPaymentAccount.detail}
        icon={savedPaymentAccount.logo_url}
        iconPadding="xxs"
        hideCheckbox
        hideChildSeparatorLine
        {...cardProps}
      >
        {children}
      </SelectableCard>
    </OfflineTooltip>
  );
};
